import {
    Select,
    SelectContent,
    SelectItem,
    SelectList,
    SelectTrigger,
    SelectValue,
    type SelectOption,
} from '@plato/ui/components/select';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type SalesRegionSelectProps = {
    value: string | undefined | null;
    onChange: (value: string | undefined | null) => void;
    options: (string | null)[];
    isLoading?: boolean;
};

const ALL_REGIONS_OPTION = '__ALL_REGIONS__';
const NO_REGION_OPTION = '__NO_REGION__';

export function SalesRegionSelect({ isLoading, value, onChange, options: regions }: SalesRegionSelectProps) {
    const { t } = useTranslation('team');

    const items = [
        { value: ALL_REGIONS_OPTION, label: t('all_regions') },
        ...regions.map((region) => ({
            value: region ?? NO_REGION_OPTION,
            label: region?.trim().length ? region : t('no_region'),
        })),
    ];

    // Two sentinels, because "every region" and "the customers without one" are both picks.
    let selected: string;

    switch (value) {
        case undefined:
            selected = ALL_REGIONS_OPTION;
            break;
        case null:
            selected = NO_REGION_OPTION;
            break;
        default:
            selected = value;
    }

    return (
        <Select
            title={t('sales_region_label')}
            items={items}
            value={selected}
            onValueChange={(next) => {
                switch (next) {
                    case ALL_REGIONS_OPTION:
                        onChange(undefined);
                        break;
                    case NO_REGION_OPTION:
                        onChange(null);
                        break;
                    default:
                        onChange(next);
                }
            }}
        >
            <SelectTrigger disabled={!isLoading && regions.length < 2} aria-label={t('sales_region_label')}>
                <MapPin className="size-4 shrink-0 text-content-weaker" />
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectList>
                    {(item: SelectOption) => <SelectItem key={item.value} value={item} />}
                </SelectList>
            </SelectContent>
        </Select>
    );
}
